import { IMarketDisplayProps, IDisplayProps } from '../../services/GraphQLShared';
import { gql, useMutation } from '@apollo/client';
import { EnumType, getIcon, IEnumProps, ProductTypeEnum, valueOf } from '../../../config/Enums';
import { Avatar, Card, CardBody } from '@progress/kendo-react-layout';
import { Col, Row } from 'react-bootstrap';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { MUTATION_ADDTRAIL } from '../../services/GraphQLPowerBI';
import { SvgIcon } from '@progress/kendo-react-common';
import { lockIcon, hyperlinkOpenIcon, infoCircleIcon, chevronRightIcon } from '@progress/kendo-svg-icons';
import { useState } from 'react';
import { RequestAccessPageUpdateDialog } from '../view/RequestAccessPageViewPage';
import { ToastType, Toast } from '../../shared/ToastNotification';
import { RequestAccessPageResources as AccessRequestResources } from '../../../config/Resources';

export interface IReportCatalogSummaryProps {
  id: string;
  reportName: string;
  code: string;
  type: string;
  embedArea: string;
  new: boolean;
  rLS: boolean;
  section: IDisplayProps;
  environment: string;
  description?: string;
  market: IMarketDisplayProps;
  formatTags: [IDisplayProps];
  display?: string | undefined;
  status: IEnumProps;
  totalPages: number;
  totalFilters: number;
  modifiedOn: Date;
  modifiedBy: string;
}
export const GET_DATACATALOG_SUMMARY = {
  query: gql`
    query GetReportCatalogSummary($search: String, $entityStatus: Int, $where: [FilterByParam], $orderBy: [OrderByParam]) {
      embedReports(search: $search, entityStatus: $entityStatus, where: $where, orderBy: $orderBy) {
        id
        reportName
        type
        display
        embedArea
        section {
          id
          display
          order
          status
        }
        environment
        description
        rLS
        appInstance
        contactOrSupportEmail
        embedded
        groups
        new
        market {
          id
          display
          isoCode
        }
        formatTags {
          id
          display
          type
        }
        totalPages
        totalFilters
      }
    }
  `,
  queryNode: 'embedReports',
  textField: 'display',
  keyField: 'id',
};


export const RenderDashboard = (props) => {
  const isLink = props.item.type == ProductTypeEnum.Link;
  const e = props.item;
  if (e.access) {
    if (isLink && !e.embedded) {
      const [mutationSynchronize] = useMutation(MUTATION_ADDTRAIL);
      return (
        <a href={e.embedArea} onClick={() => mutationSynchronize({ variables: { productId: e.id, productName: e.reportName } })} rel="noopener noreferrer" target="_blank" style={{ textDecoration: 'none' }}>
          <ProductDashboardCard {...props} />
        </a>
      );
    }
    return (
      <div onClick={() => props.navigateTo()}>
        <ProductDashboardCard {...props} />
      </div>
    );
  }
  return (
    <div>
      <ProductDashboardCard {...props} />
    </div>
  );
};

const ProductDashboardCard = (props) => {
  const e = props.item;
  const [openRequestAccess, setOpenRequestAccess] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const onRequestAccess = (item: any) => {
    setSelectedReport(item);
    setOpenRequestAccess(true);
  };
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const isLink = e.type == ProductTypeEnum.Link;
  const envChip = valueOf(e.environment, EnumType.EnvironmentType);
  const chipList =
    e.formatTags && e.formatTags.length > 0
      ? e.formatTags.map((x: any) =>
        Object.assign({
          display: x.display,
          id: x.id,
          icon: ` fa fa-${getIcon(x.type, EnumType.TagType)}`,
          color: '',
        }),
      )
      : [];
  chipList.push({ display: envChip.shortText, icon: `fa fa-${envChip.icon}`, id: envChip.code, color: envChip.color });
  return (
    <>
      <Card
        className="country-cards"
        style={{
          width: 422,
          minHeight: 112,
          background: 'transparent',
          cursor: e.access ? 'pointer' : 'default',
        }}
      >
        <CardBody className={`app-summary ${!e.access ? (e.appInstance ? 'request-access' : 'blocked') : 'has-access'}`}>
          <Row>
            <Col className="d-flex align-items-center justify-content-between ps-1">
              <div className="d-flex align-items-center">
                {e.market.isoCode !== 'XX' ? (
                  <Avatar rounded="full" type="image" size="large" border={false}>
                    <img src={`/images/icons/${e.id}`} alt={e.market.isoCode} onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = `/images/flags/1x1/${e.market.isoCode.toLowerCase()}.svg`;
                    }} />
                  </Avatar>
                ) : (
                  <Avatar rounded="full" type="image" size="large">
                    <img src={`/images/icons/${e.id}`} alt={e.market.isoCode} onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = "/images/flags/1x1/global.svg";
                    }} />
                  </Avatar>
                )}
                <Tooltip openDelay={100} position="right">
                  <h4 className="mb-0 report-header" title={e.reportName}>
                    {e.reportName}
                  </h4>
                </Tooltip>
              </div>
              {!e.access && (
                <>
                  <span className={"badge " + (e.appInstance ? "text-bg-primary" : "text-bg-danger")} onClick={(evt) => {
                    if (!e.appInstance) return;
                    evt.stopPropagation();
                    onRequestAccess(e);
                  }} style={{
                    cursor: e.appInstance ? 'pointer' : 'not-allowed',
                  }}>
                    {e.appInstance ? (
                      <>
                        Request Access
                        <SvgIcon icon={chevronRightIcon} className="external-link" />
                      </>
                    ) : ''}
                  </span>
                </>
              )}
              {e.access && (
                <>
                  <span className="badge text-bg-success available">
                    Available
                  </span>
                </>
              )}
              {e.contactOrSupportEmail && (
                <Tooltip openDelay={100} position="right" anchorElement="target" parentTitle={true}>
                  <a href={e.contactOrSupportEmail} title="Click here for more information on this product" onClick={(e) => e.stopPropagation()} rel="noopener noreferrer" target="_blank" style={{ textDecoration: 'none' }}>
                    <SvgIcon icon={infoCircleIcon} className="ms-2 external-link" />
                  </a>
                </Tooltip>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={2}></Col>
            <Col className="ps-1">
              <Tooltip openDelay={100} position="right">
                {e.new && (
                  <span className="badge rounded-pill text-bg-success new-state">New</span>
                )}
                <span className="report-desc" title={e.description}>
                  {e.description}
                </span>
              </Tooltip>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {isLink && !e.embedded && <SvgIcon icon={hyperlinkOpenIcon} className="ms-2 external-link external-link-icon" />}
            </Col>
          </Row>
        </CardBody>
      </Card>
      {selectedReport && (
        <RequestAccessPageUpdateDialog
          visible={openRequestAccess}
          dataItem={selectedReport}
          onClickOk={() => {
            setOpenRequestAccess(false);
            setToast({
              message: AccessRequestResources.SuccessMessage,
              type: 'success',
            });
          }}
          onClickCancel={() => setOpenRequestAccess(false)}
        />
      )}

      <Toast
        message={toast?.message ?? ''}
        type={toast?.type}
        onClose={() => setToast(null)}
      />
    </>
  );
};
